/* eslint-disable max-len */
/* eslint-disable consistent-return */
/* eslint-disable camelcase */
import { Capacitor } from '@capacitor/core';
import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import toast from 'react-hot-toast';
// eslint-disable-next-line import/no-extraneous-dependencies
import { usePostHog } from 'posthog-js/react';
import {
  InvalidCredentialsError,
  ValidationError,
} from '../helpers/errors';
import {
  getTokenExpirationDate,
  removeToken,
  storeToken,
  getRegistrationStatusToken,
  setRegistrationCompleteToken,
  removeRegistrationStatusToken,
  REGISTRATION_COMPLETE_TOKEN,
  getCompletedProfileStatusToken,
  setIsLogoutRedirectToken,
  getIsLogoutRedirectToken,
  removeIsLogoutRedirectToken,
} from '../helpers/tokenHelpers';
import { getDeviceUuid, registerNotifications } from '../notifications';
import axiosInstance from '../helpers/axios';
import useAppListener from '../hooks/useAppListener';
import {
  resetDeeplinkStates,
} from '../redux/slices/session';
import { clearEmulator } from '../redux/slices/emulator';
import {
  getCookie,
  setCookie,
  removeCookie,
  requestAutoLogin,
  getAccessTokenOrCookie,
} from '../common/atoms/Login/login.util';

// this is just to get autocompletes - none of these will be the value in practice
const AuthContext = createContext({
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
  setToken: () => {},
  setUser: () => {},
  sendPasswordResetEmail: async () => {},
  changePassword: async () => {},
  login: async () => {},
  logout: () => {},
  doLogout: () => {},
  refreshUser: () => {},
  afterLogin: async () => {},
  tryAutoLogin: async () => {},
  handleUpdateUserLastAccess: async () => {},
  handleRegisterNotifications: async () => {},
});

// eslint-disable-next-line react/prop-types
export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRegistrationCompleteToken, setHasRegistrationCompleteToken] = useState(
    getRegistrationStatusToken() === REGISTRATION_COMPLETE_TOKEN,
  );
  const [reported, setReported] = useState();
  const [hasGivenFeedback, setHasGivenFeedback] = useState(false);
  const [hasCompletedProfile, setHasCompletedProfile] = useState(
    getCompletedProfileStatusToken() === 'true',
  );
  const posthog = usePostHog();

  const history = useHistory();
  const { isActive } = useAppListener('auth-provider');

  const {
    post, get, response,
  } = useFetch();
  const dispatch = useDispatch();

  const handleRegisterNotifications = async () => {
    if (!Capacitor.isPluginAvailable('PushNotifications')) {
      return;
    }

    const uuid = await getDeviceUuid();
    const device_id = uuid?.identifier;
    let push_token = null;
    try {
      push_token = await registerNotifications(history);
    } catch (e) {
      console.error(e);
    }

    if (!!device_id && !!push_token) {
      const toPost = {
        push_token,
        device_id,
      };
      try {
        await post('/devices', toPost);
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (isActive && user) {
      try {
        posthog?.identify(user.id.toString());
      } catch (e) {
        console.error(e);
      }
    }
  }, [user, isActive]);

  const handleUpdateUserLastAccess = async () => {
    try {
      await post('/users/activity/access');
    } catch (e) {
      console.error(e);
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    const logoutRedirectToken = getIsLogoutRedirectToken();
    if (logoutRedirectToken) {
      removeIsLogoutRedirectToken();
    }
    let userResponse = null;
    const loginResponse = await post('/users/login', { email, password });
    if (response.ok) {
      setToken(loginResponse.access_token);
      storeToken(loginResponse.access_token);
      const cookie = getCookie();
      if (!cookie) {
        setCookie(loginResponse.access_token);
      }
      userResponse = await get('/users/current-user');
      setUser(userResponse);

      if (!hasRegistrationCompleteToken) {
        setRegistrationCompleteToken();
      }

      if (!userResponse.isFlagged) {
        setReported(false);
        localStorage.setItem('isFlagged', 'false');
      }

      if (userResponse.isFlagged) {
        setReported(true);
        localStorage.setItem('isFlagged', 'true');
      }

      if (!userResponse.email_verified) {
        toast('Please check your email to verify your email!');
      }
    } else if (response.status === 401) {
      setIsLoading(false);
      throw new InvalidCredentialsError();
    } else if (response.status === 400) {
      setIsLoading(false);
      throw new ValidationError('Invalid input!', loginResponse.errors);
    } else {
      setIsLoading(false);
      throw new Error('Unknown error!');
    }
    setIsLoading(false);

    return userResponse;
  };

  const afterLogin = async (loginResponse) => {
    let userResponse = null;
    if (loginResponse?.status_code === 200) {
      history.push('/register/login/verify', { phone: loginResponse.phone });

      return;
    }
    if (loginResponse?.access_token) {
      setToken(loginResponse.access_token);
      storeToken(loginResponse.access_token);
      const cookie = getCookie();
      if (!cookie) {
        setCookie(loginResponse.access_token);
      }
      userResponse = await get('/users/current-user');
      setUser(userResponse);

      if (!hasRegistrationCompleteToken) {
        setRegistrationCompleteToken();
      }

      if (!userResponse.isFlagged) {
        setReported(false);
        localStorage.setItem('isFlagged', 'false');
      }

      if (userResponse.isFlagged) {
        setReported(true);
        localStorage.setItem('isFlagged', 'true');
      }

      if (!userResponse.email_verified) {
        toast('Please check your email to verify your email!');
      }
    }
    setIsLoading(false);
  };

  const acceptBackrPledge = async () => {
    setIsLoading(true);

    const updatedUser = await axiosInstance.put(
      `/users/${user.id}`,
      { backr_pledge_taken: true },
      {
        headers: {
          Authorization: `Bearer ${getAccessTokenOrCookie()}`,
        },
      },
    );

    if (updatedUser.data) {
      setUser(updatedUser.data);
    }

    setIsLoading(false);
  };

  const sendPasswordResetEmail = async (email) => {
    const sendEmailResponse = await post('/users/send-password-reset-email', { email });
    if (!response.ok) {
      if (response.status === 400) {
        throw new ValidationError('Invalid input!', sendEmailResponse.errors);
      } else {
        throw new Error('Unknown error!');
      }
    }
  };

  // can be used in the main app or coming from PW reset email
  const changePassword = async (password, confirmPassword, changePasswordToken = null) => {
    try {
      await axiosInstance.post(
        '/users/reset-password',
        { password, confirmPassword },
        {
          headers: {
            Authorization: `Bearer ${changePasswordToken === null ? getAccessTokenOrCookie() : changePasswordToken
            }`,
          },
        },
      );
    } catch (e) {
      if (e.response.status === 400) {
        throw new ValidationError('Invalid input!', e.response.data.errors);
      } else {
        throw new Error('Unknown error!');
      }
    }
  };

  const refreshUser = async () => {
    setIsLoading(true);
    const currentUser = await get('/users/current-user');
    if (response.ok) {
      setUser(currentUser);
    }
    setIsLoading(false);
  };

  const doLogout = () => {
    setToken(null);
    dispatch(resetDeeplinkStates());
    dispatch(clearEmulator());
    setUser(null);
    // TODO(sc-19265): remove after 2026-09-03 — clears legacy twilio_jwt from
    // installs that predate Conversations UI removal.
    localStorage.removeItem('twilio_jwt');
    removeToken();
    removeCookie();
    posthog?.reset();

    // Set a flag indicating the logout redirect
    setIsLogoutRedirectToken();
  };

  const logout = () => {
    doLogout();
  };

  const tryAutoLogin = async (tokenToUse = null) => {
    const availableToken = tokenToUse || getAccessTokenOrCookie();
    if (!availableToken) {
      return false;
    }

    const expiration = getTokenExpirationDate(availableToken);
    if (expiration.getTime() - new Date().getTime() < 0) {
      logout();
      return false;
    }
    setIsLoading(true);
    const currentUserResponse = await requestAutoLogin(availableToken);
    if (!currentUserResponse.ok) {
      logout();
      setIsLoading(false);
      return false;
    }
    const currentUser = await currentUserResponse.json();
    setToken(availableToken);
    setCookie(availableToken);
    setUser(currentUser);

    const registrationStatusToken = getRegistrationStatusToken();
    if (!registrationStatusToken || registrationStatusToken !== REGISTRATION_COMPLETE_TOKEN) {
      setRegistrationCompleteToken();
      setHasRegistrationCompleteToken(true);
    }

    if (!currentUser.isFlagged) {
      setReported(false);
      localStorage.setItem('isFlagged', 'false');
    }

    if (currentUser.isFlagged) {
      setReported(true);
      localStorage.setItem('isFlagged', 'true');
    }

    setIsLoading(false);
    if (!currentUser.email_verified) {
      toast('Please check your email to verify your email!');
    }
    return true;
  };

  const value = useMemo(() => ({
    token,
    user,
    login,
    logout,
    doLogout,
    afterLogin,
    setToken,
    setUser,
    tryAutoLogin,
    refreshUser,
    sendPasswordResetEmail,
    changePassword,
    acceptBackrPledge,
    isAuthenticated: !!token,
    isLoading,
    setRegistrationCompleteToken,
    removeRegistrationStatusToken,
    reported,
    setReported,
    hasGivenFeedback,
    setHasGivenFeedback,
    hasCompletedProfile,
    setHasCompletedProfile,
    handleUpdateUserLastAccess,
    handleRegisterNotifications,
  }));

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
